import { useAIAssistantForPage } from '@euler/app/components/ai/pageContext';
import { Typography } from '@euler/app/components/typography';
import { ReportScreens } from '@euler/app/flows/report/ReportScreens';
import {
  ReportContextProvider,
  useReportContext,
} from '@euler/app/flows/report/components/ReportContext';
import { ReportScrollView } from '@euler/app/flows/report/components/ReportScrollView';
import { ReportRef } from '@euler/app/flows/report/functions';
import { useReportHeaderMetrics } from '@euler/app/flows/report/functions/useReportHeaderMetrics';
import {
  projectionForReportTab,
  reportTabKeyFromScreen,
} from '@euler/app/flows/report/functions/util';
import { ProductManifestProps } from '@euler/app/flows/report/types';
import { EmptyView } from '@euler/components/EmptyView';
import { FontFamily } from '@euler/components/typography';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { spreadScreens } from '@euler/functions/spreadScreens';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { StoreFeature } from '@euler/model/enum';
import { VehicleReportProjection } from '@euler/model/report';
import { ReportTabKey } from '@euler/model/viewmodel';
import { usePersistFn } from '@euler/utils/hooks';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import { memo } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { useTheme } from 'styled-components';

const Tab = createMaterialTopTabNavigator();

const kTransparentStyle = {
  backgroundColor: 'transparent',
};

type Props = {
  title?: string;
  target: ReportRef;
  initialTab?: keyof VehicleReportProjection;
  productManifest?: ProductManifestProps;
  onShare?: (tab?: ReportTabKey) => void;
} & { [p in ReportTabKey]?: boolean };

export const ReportScreen = wrapNavigatorScreen(
  (props: Props) => {
    useAIAssistantForPage('report', {
      orderNo: props.target.orderNo,
      reportNo: props.target.reportNo,
    });
    return (
      <ReportContextProvider
        target={props.target}
        projection={projectionForReportTab(undefined)}
        offsetTop={0}
        suspenseFallback={undefined}
      >
        <Content {...props} />
      </ReportContextProvider>
    );
  },
  props => ({
    headerShown: false,
    title: props.route.params.title ?? '报告详情',
  }),
);

const Content = memo(
  ({
    title,
    target,
    initialTab,
    productManifest,
    onShare,
    ...props
  }: Props) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const isMobile = useIsMobileLayout();
    const { headerHeight } = useReportHeaderMetrics();
    const tabContainerStyle = isMobile ? undefined : kTransparentStyle;
    const samplingCheck = useStoreFeature(StoreFeature.SamplingCheck).enabled;
    const {
      report: { status },
    } = useReportContext();

    const screens = Object.fromEntries(
      Object.entries(ReportScreens).filter(
        ([key]) =>
          (props as any)[reportTabKeyFromScreen(key as any)] !== false &&
          status[reportTabKeyFromScreen(key as any)] &&
          (key !== 'samplingCheckReport' || samplingCheck),
      ),
    );

    const handleShareWholeReport = usePersistFn(() => {
      onShare?.();
    });

    // Which tab opens first. Must feed the navigator's `initialRouteName` —
    // without it the navigator always lands on the first screen
    // (preInspection), regardless of the requested `initialTab`.
    const initialScreen = initialScreenFromInitialTab(
      initialTab ?? 'preInspection',
    );

    return (
      <ReportScrollView
        target={target}
        title={title}
        transparentHeader={!isMobile}
        rightOptions={
          onShare
            ? () => (
                <TouchableOpacity onPress={handleShareWholeReport}>
                  <Typography.Label color={'#fff'} size={rpx(14)}>
                    分享报告
                  </Typography.Label>
                </TouchableOpacity>
              )
            : undefined
        }
        renderScrollView={(_, paddingTop) => (
          <View
            css={`
              padding-top: ${paddingTop}px;
              padding-bottom: 0;
              flex: 1;
            `}
          >
            {Object.entries(screens).length ? (
              <Tab.Navigator
                initialRouteName={initialScreen}
                sceneContainerStyle={tabContainerStyle}
                pagerStyle={tabContainerStyle}
                style={tabContainerStyle}
                screenOptions={{
                  lazy: true,
                  tabBarStyle: isMobile
                    ? undefined
                    : {
                        backgroundColor: 'transparent',
                        position: 'absolute',
                        zIndex: 1000,
                        top: headerHeight,
                        left: 0,
                        right: 0,
                        borderBottomColor: 'rgba(255, 255, 255, 0.7)',
                        borderBottomWidth: 1,
                      },
                  tabBarActiveTintColor: isMobile
                    ? theme.report.tab.activeColor
                    : '#ffffff',
                  tabBarInactiveTintColor: isMobile
                    ? theme.report.tab.inactiveColor
                    : 'rgba(255, 255, 255, 0.7)',
                  tabBarIndicatorContainerStyle:
                    Object.keys(screens).length === 1
                      ? {
                          maxWidth: '50%',
                        }
                      : undefined,
                  tabBarItemStyle:
                    Object.keys(screens).length === 1
                      ? {
                          maxWidth: '50%',
                        }
                      : undefined,
                  tabBarLabel: ({ focused, children }) => (
                    <Text
                      numberOfLines={1}
                      css={`
                        font-family: ${focused
                          ? FontFamily.NotoSans.Bold
                          : FontFamily.NotoSans.Medium};
                        font-size: ${rpx(14.5)}px;
                        color: ${focused
                          ? isMobile
                            ? theme.report.tab.activeColor
                            : '#fff'
                          : isMobile
                          ? theme.report.tab.inactiveColor
                          : 'rgba(255, 255, 255, 0.75)'};
                      `}
                    >
                      {children}
                    </Text>
                  ),
                  tabBarLabelStyle: {
                    fontSize: rpx(16),
                    fontFamily: FontFamily.NotoSans.Regular,
                  },
                  tabBarIndicatorStyle: {
                    height: rpx(2.5),
                    marginBottom: -1,
                    backgroundColor: isMobile
                      ? theme.report.tab.activeColor
                      : '#fff',
                  },
                }}
              >
                {spreadScreens(Tab.Screen, screens, initialScreen, () => ({
                  standalone: false,
                  target,
                  productManifest,
                  onShare,
                }))}
              </Tab.Navigator>
            ) : (
              <EmptyView
                message="暂无相关报告内容"
                iconColor="#bbb"
                messageStyle={{ color: '#999' }}
                css={`
                  background-color: #fff;
                `}
              />
            )}
          </View>
        )}
      />
    );
  },
);

function initialScreenFromInitialTab(
  initialTab: keyof VehicleReportProjection,
): keyof typeof ReportScreens {
  if (initialTab === 'preInspection') {
    return 'preInspectionReport';
  }
  if (initialTab === 'inspection' || initialTab === 'obdInspection') {
    return 'inspectionReport';
  }
  if (initialTab === 'construction') {
    return 'constructionReport';
  }
  if (initialTab === 'samplingCheck') {
    return 'samplingCheckReport';
  }
  return 'deliveryCheckReport';
}
